import { track } from '@amplitude/analytics-browser';
import { Form, Formik } from 'formik';
import React, { ReactElement } from 'react';
import * as Yup from 'yup';

import { WEB_APP_SIGNUP_URL } from '../../../../../globals';
import { useSignUpHandler } from '../../../../../hooks/useSignUpHandler';
import { Assets } from '../../../../../utils/assets';
import { Input } from '../../../../forms/Input';
import MaxWidth from '../../../../shared/MaxWidth';
import { SOCIAL_CARDS } from '../../constants';
import { AdhdCard } from '../AdhdCard';
import { AdhdScienceCardScroll } from '../AdhdScienceCardScroll';
import { InformationCard } from '../InformationCard';
import { SocialCard } from '../SocialCard';
import * as S from './Content.styles';

export const Content = (): ReactElement => {
  const onSignUp = useSignUpHandler();

  const handleClickCta = () => {
    window.location.href = WEB_APP_SIGNUP_URL;
  };

  const handleReadNatureStudy = () => {
    window.open("https://www.nature.com/articles/s42003-024-07026-3", "_blank");
  };

  const validationSchema = Yup.object({
    email: Yup.string().email().required('Email is required'),
  });

  const handleOnSubmit = (values) => {
    if (values.email) {
      localStorage.setItem('prefillEmail', values.email);
    }
    track('test_signup_email');
    onSignUp();
  };
  
  return (
    <S.Wrapper>
      <S.FocusSection>
        <S.LeftBlurImage 
          src="https://cdn.brain.fm/images/music_for_adhd_blur.png"
        />
        <MaxWidth>
          <S.FocusInfoContent>
            <S.SectionTitle>
              When Focus <br /> Feels Impossible
            </S.SectionTitle>
            <S.FocusInfoCardsWrapper>
              <InformationCard 
                description="Motivation and discipline are important, but on a biological level your brain needs to be operating in ways that can allow you to find your flow." 
                imageSrc="https://cdn.brain.fm/images/music-for-adhd-focus-info-1.webp"
                title="It's not you, it's your brain" 
              />
              <InformationCard 
                description="Most music is designed to grab your attention, not help you focus. Lyrics and catchy melodies can pull your brain away from deep work." 
                imageSrc="https://cdn.brain.fm/images/music-for-adhd-focus-info-2.webp"
                title="Normal music might be making it harder" 
              />
              <InformationCard 
                description="ADHD brains need stimulation to work best, but the key is finding music that keeps your mind engaged without pulling you off task. " 
                imageSrc="https://cdn.brain.fm/images/music-for-adhd-focus-info-3.webp"
                title="You need stimulation *without* distraction"
              />
            </S.FocusInfoCardsWrapper>
          </S.FocusInfoContent>

          <S.TryContent>
            <S.TryImage src="https://cdn.brain.fm/images/music-for-adhd-focus-sign-up.webp" />
            <S.TrySignUpWrapper>
              <S.TryTitle>
                Sign up to get 14 days of free science-backed music for ADHD 
              </S.TryTitle>
              <S.TryDescription>
                Try music made from the ground up for ADHD brains.
              </S.TryDescription>
              <S.TrySignUpFormWrapper>
                <Formik
                  initialValues={{ email: '' }}
                  validationSchema={validationSchema}
                  onSubmit={handleOnSubmit}
                >
                  {({ handleChange, handleBlur, values }) => (
                    <Form>
                      <S.TrySignUpForm key="try-signup-form">
                        <Input
                          label="Your Email"
                          name="email"
                          placeholder="Your Email"
                          type="email"
                          value={values.email}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                        <S.ListenNowButton
                          type="submit"
                        >
                          TRY BRAIN.FM FOR FREE
                        </S.ListenNowButton>
                      </S.TrySignUpForm>
                    </Form>
                  )}
                </Formik>
              </S.TrySignUpFormWrapper>
            </S.TrySignUpWrapper>
          </S.TryContent>

          <S.AdhdInfoContent>
            <S.AdhdHeader>
              <S.SectionTitle>
                What is ADHD?
              </S.SectionTitle>
              <S.SectionSmallDescription>
                Affecting an estimated 5% of people worldwide, ADHD is a neurodevelopment condition characterized by persistent patterns of inattention that interfere with daily life.
              </S.SectionSmallDescription>
            </S.AdhdHeader>
            <S.AdhdCardsWrapper>
              <AdhdCard 
                description="People with ADHD often struggle to maintain focus on tasks; this can lead to unfinished projects, difficulty following conversations, and challenges in academic or work settings. Distraction and forgetfulness are common, making organization and time management difficult."
                imageSrc="https://cdn.brain.fm/images/adhd-social-head.webp"
                title="Difficulty with sustained attention"
              />
              <AdhdCard 
                description="Research suggests individuals with ADHD have distinct brainwave patterns in areas related to impulse control and attention regulation. Differences in beta brainwave activity can affect cognitive processing, leading to difficulties in concentration and task switching."
                imageSrc="https://cdn.brain.fm/images/adhd-audio-wave.webp"
                title="Brainwave differences"
              />
              <AdhdCard 
                description="ADHD is linked to differences in dopamine regulation, a neurotransmitter for motivation and reward processing. Lower dopamine levels make it hard to stay engaged without immediate gratification, leading to difficulties in maintaining motivation and focus."
                imageSrc="https://cdn.brain.fm/images/adhd-brain.webp"
                title="Dopamine Imbalance"
              />
            </S.AdhdCardsWrapper>
          </S.AdhdInfoContent>

          <S.ScienceOfAdhdContent>
            <S.ScienceOfAdhdHeader>
              <S.SectionTitle>
                The Science of ADHD Music
              </S.SectionTitle>
              <S.SectionSmallDescription>
                Not all music helps ADHD, but Brain.fm is made specifically for this purpose.
              </S.SectionSmallDescription>
            </S.ScienceOfAdhdHeader>
            <AdhdScienceCardScroll />
            <S.ScienceOfAdhdButton onClick={handleClickCta}>
              TRY BRAIN.FM, FEEL THE DIFFERENCE
            </S.ScienceOfAdhdButton>
          </S.ScienceOfAdhdContent>


          <S.StudyContent>
            <S.StudyLeftWrapper>
              <S.StudyNewPeerText>NEW PEER-REVIEWED PUBLICATION</S.StudyNewPeerText>
              <S.StudyTitle>Rapid modulation in music supports attention in listeners with attentional difficulties.</S.StudyTitle>
              <S.StudyDescription>
                Our latest peer-reviewed publication in <span>Nature Communications Biology</span> shows how Brain.fm&apos;s technology affects the brain and performance, boosting focus brainwaves by 119%+ and improving sustained attention in people with ADHD symptoms.
              </S.StudyDescription>
              <S.ButtonLink onClick={handleReadNatureStudy}>
                <span>Read the Study on Nature.com</span>
                <img alt="arrow" src="https://cdn.brain.fm/icons/arrow-right.svg" />
              </S.ButtonLink>
            </S.StudyLeftWrapper>
            <S.StudyImage src="https://cdn.brain.fm/images/adhd-peer-study.webp" />
            <S.StudyBlurImage  src="https://cdn.brain.fm/images/music_for_adhd_blur.png"
        />
        </S.StudyContent>

        <S.HelpContent>
          <S.HelpTitle>
            How can Brain.fm help me?
          </S.HelpTitle>
          <S.HelpImage src="https://cdn.brain.fm/images/focus-desk-wave.webp" />
          <S.HelpCardsWrapper>
            <AdhdCard 
                description="Get into deep work mode in minutes. Brain.fm's science-backed audio syncs your brainwaves, so you can zone in fast."
                imageSrc="https://cdn.brain.fm/images/adhd-zap.webp"
                title="Focus Faster"
              />
              <AdhdCard 
                description="Stay in the zone without burnout. Brain.fm's soundscapes keep you engaged and distraction-free."
                imageSrc="https://cdn.brain.fm/images/adhd-timer.webp"
                title="Focus Longer"
              />
              <AdhdCard 
                description="Make work feel effortless.  Brain.fm helps you stay focused and engaged, so productivity becomes a pleasure."
                imageSrc="https://cdn.brain.fm/images/adhd-smile.webp"
                title="Enjoy Getting Things Done"
              />
          </S.HelpCardsWrapper>
        </S.HelpContent>
        </MaxWidth>
      </S.FocusSection>
      <MaxWidth>
        <S.HowToGetStartedSection>
          <S.HowToGetStartedImage
              src="https://cdn.brain.fm/images/focus-on-cloud.webp"
            />
          <S.HowToGetStartedContent>
            <S.SectionTitle>
              How to Get Started
            </S.SectionTitle>
            <S.StepsContainer>
              <S.StepItem>
                <S.StepImage alt={Assets.icons.squareUserIcon.alt} src={Assets.icons.squareUserIcon.url} />
                <S.StepContent>
                  <S.StepTitle>Sign up</S.StepTitle>
                  <S.StepDescription>Sign up with an email address to get an account.</S.StepDescription>
                </S.StepContent>
              </S.StepItem>
              <S.StepItem>
                <S.StepImage alt={Assets.icons.notebookPen.alt} src={Assets.icons.notebookPen.url} />
                <S.StepContent>
                  <S.StepTitle>Take the quiz</S.StepTitle>
                  <S.StepDescription>We’ll learn about your brain and start personalizing your music.</S.StepDescription>
                </S.StepContent>
              </S.StepItem>
              <S.StepItem>
                <S.StepImage alt={Assets.icons.timer.alt} src={Assets.icons.timer.url} />
                <S.StepContent>
                  <S.StepTitle>Work for 30 minutes</S.StepTitle>
                  <S.StepDescription>Sit down with a task and just hit play.</S.StepDescription>
                </S.StepContent>
              </S.StepItem>
            </S.StepsContainer>
            <S.ButtonLink onClick={handleClickCta}>
                <span>GET STARTED</span>
                <img alt="arrow" src="https://cdn.brain.fm/icons/arrow-right.svg" />
              </S.ButtonLink>
          </S.HowToGetStartedContent>
        </S.HowToGetStartedSection>
        
        <S.ReviewsSection>
          <S.ReviewsTitle>
            Reviews from ADHD listeners
          </S.ReviewsTitle>
          <S.ReviewsDescription>
            See what real users with ADHD have to say about Brain.fm
          </S.ReviewsDescription>
          <S.SocialCardsWrapper>
            {SOCIAL_CARDS.map((card, index) => (
              <SocialCard
                key={index}
                description={card.description}
                name={card.name}
                profileImageUrl={card.profileImageUrl}
                username={card.username}
              />
            ))}
          </S.SocialCardsWrapper>
          <S.ButtonLink onClick={handleClickCta}> 
            <span>HEAR IT FOR YOURSELF</span>
            <img alt="arrow" src="https://cdn.brain.fm/icons/arrow-right.svg" />
          </S.ButtonLink>
        </S.ReviewsSection>
      </MaxWidth>
    </S.Wrapper>
  );
};
